import { useCounter } from '@/components/common/Notifications/useCounter'
import type { StepRenderProps } from '@/components/new-safe/CardStepper/useCardStepper'
import type { NewSafeFormData } from '@/components/new-safe/create'
import { getRedirect } from '@/components/new-safe/create/logic'
import StatusMessage from '@/components/new-safe/create/steps/StatusStep/StatusMessage'
import useUndeployedSafe from '@/components/new-safe/create/steps/StatusStep/useUndeployedSafe'
import { lightPalette } from '@safe-global/theme/palettes'
import { AppRoutes } from '@/config/routes'
import { safeCreationPendingStatuses } from '@/features/counterfactual'
import { SafeCreationEvent, safeCreationSubscribe, isPredictedSafeProps } from '@/features/counterfactual/services'
import { useCurrentChain } from '@/hooks/useChains'
import Rocket from '@/public/images/common/rocket.svg'
import { CREATE_SAFE_EVENTS, trackEvent } from '@/services/analytics'
import { useAddNewSafeToUrlSpace } from '@/features/spaces'
import { getNewSafeHomeUrl } from '../ReviewStep/utils'
import { Alert, AlertTitle, AlertDescription } from '@safe-global/views/components/ui/alert'
import { Button } from '@safe-global/views/components/ui/button'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { useEffect, useRef, useState } from 'react'
import { getLatestSafeVersion } from '@safe-global/utils/utils/chains'

const SPEED_UP_THRESHOLD_IN_SECONDS = 15

export const CreateSafeStatus = ({ setProgressColor, setStep, setStepData }: StepRenderProps<NewSafeFormData>) => {
  const [status, setStatus] = useState<SafeCreationEvent>(SafeCreationEvent.PROCESSING)
  const [safeAddress, pendingSafe] = useUndeployedSafe()
  const router = useRouter()
  const chain = useCurrentChain()
  const addNewSafeToUrlSpace = useAddNewSafeToUrlSpace()
  const hasRedirected = useRef(false)

  const counter = useCounter(pendingSafe?.status.submittedAt)

  const isError = status === SafeCreationEvent.FAILED || status === SafeCreationEvent.REVERTED

  useEffect(() => {
    const unsubFns = Object.entries(safeCreationPendingStatuses).map(([event]) =>
      safeCreationSubscribe(event as SafeCreationEvent, async () => {
        setStatus(event as SafeCreationEvent)
      }),
    )

    return () => {
      unsubFns.forEach((unsub) => unsub())
    }
  }, [])

  useEffect(() => {
    if (!chain || !safeAddress || status !== SafeCreationEvent.SUCCESS || hasRedirected.current) return
    hasRedirected.current = true

    const getStepUpReturnUrl = (spaceId: string) => getNewSafeHomeUrl(chain.shortName, safeAddress, spaceId)
    addNewSafeToUrlSpace(chain.chainId, safeAddress, getStepUpReturnUrl).then(({ spaceId, isStepUpPending }) => {
      if (isStepUpPending) return
      const redirect = getRedirect(chain.shortName, safeAddress, spaceId, router.query?.safeViewRedirectURL)
      if (typeof redirect !== 'string' || redirect.startsWith('/')) {
        router.push(redirect)
      }
    })
  }, [addNewSafeToUrlSpace, chain, router, safeAddress, status])

  useEffect(() => {
    if (!setProgressColor) return

    if (isError) {
      setProgressColor(lightPalette.error.main)
    } else {
      setProgressColor(lightPalette.secondary.main)
    }
  }, [isError, setProgressColor])

  const tryAgain = () => {
    trackEvent(CREATE_SAFE_EVENTS.RETRY_CREATE_SAFE)

    if (!pendingSafe || !isPredictedSafeProps(pendingSafe.props)) {
      setStep(0)
      return
    }

    setProgressColor?.(lightPalette.secondary.main)
    setStep(2)
    setStepData?.({
      owners: pendingSafe.props.safeAccountConfig.owners.map((owner) => ({ name: '', address: owner })),
      name: '',
      networks: [],
      threshold: pendingSafe.props.safeAccountConfig.threshold,
      saltNonce: Number(pendingSafe.props.safeDeploymentConfig?.saltNonce),
      safeAddress,
      safeVersion: pendingSafe.props.safeDeploymentConfig?.safeVersion ?? getLatestSafeVersion(chain),
    })
  }

  const onCancel = () => {
    trackEvent(CREATE_SAFE_EVENTS.CANCEL_CREATE_SAFE)
  }

  return (
    <div className="bg-card text-card-foreground overflow-hidden rounded-xl text-center">
      <div className="p-4 sm:p-16">
        <StatusMessage status={status} isError={isError} pendingSafe={pendingSafe} />

        {counter && counter > SPEED_UP_THRESHOLD_IN_SECONDS && !isError && (
          <Alert variant="warning" outlined={false} className="mt-10">
            <Rocket />
            <AlertTitle className="text-left font-bold">Transaction is taking too long</AlertTitle>
            <AlertDescription className="text-left">
              Try to speed it up with better gas parameters in your wallet.
            </AlertDescription>
          </Alert>
        )}

        {isError && (
          <div className="flex flex-row justify-center gap-4">
            <Button variant="outline" onClick={onCancel} render={<Link href={AppRoutes.index} />}>
              Go to homepage
            </Button>
            <Button variant="default" onClick={tryAgain}>
              Try again
            </Button>
          </div>
        )}
      </div>
    </div>
  )
}
